import { useEffect, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { X } from "lucide-react";
import { Composer } from "@/components/ui/Composer";
import { useGaiaChat } from "@/features/chat/useGaiaChat";
import styles from "@/features/chat/ChatScreen.module.css";

const GREETING = "Hello. How can I help with your pods?";

export function ChatScreen() {
  const [searchParams] = useSearchParams();
  const initialQuestion = searchParams.get("q") ?? "";

  const { messages, isThinking, error, send } = useGaiaChat({ greeting: GREETING });
  const bottom = useRef<HTMLDivElement>(null);
  const askedInitial = useRef(false);

  // The question typed on the home screen arrives in the URL and is asked once. The ref, not the
  // dependency list, is what makes it once: StrictMode runs effects twice on purpose.
  useEffect(() => {
    if (askedInitial.current || initialQuestion === "") return;
    askedInitial.current = true;
    void send(initialQuestion);
  }, [initialQuestion, send]);

  useEffect(() => {
    bottom.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isThinking]);

  return (
    <div className={styles.page}>
      <div className={styles.topBar}>
        <Link to="/" className={styles.close}>
          <X className="h-3.5 w-3.5" aria-hidden />
          Close
        </Link>
        <span className={styles.gaiaLabel}>GAIA</span>
      </div>

      <div className={styles.messages}>
        {messages.map((message, index) => (
          <div
            // Messages are append-only and never reordered, so the index is stable for the life of
            // the conversation. Content is not unique - the same question may be asked twice.
            key={index}
            className={`${styles.message} ${message.role === "user" ? styles.user : styles.assistant}`}
          >
            <span className={styles.sender}>{message.role === "user" ? "YOU" : "GAIA"}</span>
            <div className={styles.bubble}>{message.content}</div>
          </div>
        ))}

        {isThinking && (
          <div className={`${styles.message} ${styles.assistant}`}>
            <span className={styles.sender}>GAIA</span>
            <div className={styles.bubble}>
              <span className={styles.thinking}>GAIA is thinking…</span>
            </div>
          </div>
        )}

        {error !== null && (
          <p role="alert" className={styles.error}>
            {error}
          </p>
        )}

        <div ref={bottom} />
      </div>

      <div className={styles.inputArea}>
        <Composer
          placeholder="Ask GAIA anything"
          autoFocus
          busy={isThinking}
          className="mx-auto max-w-[760px]"
          onSend={(text) => {
            void send(text);
          }}
        />
      </div>
    </div>
  );
}
